import React, { createContext, useContext, useState, useEffect } from "react";

export interface NeckEditorValues {
  scaleLength: number;
  stringSpacing: number;
  nutWidth: number;
  paddingLeft: number;
  paddingTop: number;
  targetBoardWidth: number;
  noteRadius: number;
  fontScale: number;
}

const DEFAULTS: NeckEditorValues = {
  scaleLength: 1800,
  stringSpacing: 48,
  nutWidth: 10,
  paddingLeft: 80,
  paddingTop: 40,
  targetBoardWidth: 1350,
  noteRadius: 19,
  fontScale: 1,
};

interface NeckEditorContextType {
  values: NeckEditorValues;
  setValues: React.Dispatch<React.SetStateAction<NeckEditorValues>>;
  visible: boolean;
  setVisible: (v: boolean) => void;
  reset: () => void;
}

const NeckEditorContext = createContext<NeckEditorContextType>({
  values: DEFAULTS,
  setValues: () => {},
  visible: false,
  setVisible: () => {},
  reset: () => {},
});

export const useNeckEditor = () => useContext(NeckEditorContext);

export const NeckEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [values, setValues] = useState<NeckEditorValues>(() => {
    try {
      const saved = localStorage.getItem("bordona-neck-editor");
      if (saved) return { ...DEFAULTS, ...JSON.parse(saved) };
    } catch {}
    return DEFAULTS;
  });

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    localStorage.setItem("bordona-neck-editor", JSON.stringify(values));
  }, [values]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey && e.shiftKey) {
        e.preventDefault();
        setVisible((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const reset = () => setValues(DEFAULTS);

  return (
    <NeckEditorContext.Provider value={{ values, setValues, visible, setVisible, reset }}>
      {children}
    </NeckEditorContext.Provider>
  );
};
